import React, { forwardRef } from 'react';
import { 
  Snackbar, 
  SnackbarContent, 
  IconButton, 
  Box, 
  Typography, 
  styled 
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { SnackbarContent as NotistackContent, CustomContentProps, useSnackbar } from 'notistack';

// Colores personalizados para cada tipo de notificación
const colors = {
  success: {
    bg: '#E8F5E9',
    color: '#1B5E20',
    icon: <CheckCircleIcon sx={{ color: '#1B5E20' }} />
  },
  error: {
    bg: '#FFEBEE',
    color: '#B71C1C',
    icon: <ErrorIcon sx={{ color: '#B71C1C' }} />
  },
  warning: {
    bg: '#FFF8E1',
    color: '#F57F17',
    icon: <WarningIcon sx={{ color: '#F57F17' }} />
  },
  info: {
    bg: '#E3F2FD',
    color: '#0D47A1',
    icon: <InfoIcon sx={{ color: '#0D47A1' }} />
  },
  default: {
    bg: '#FFFFFF',
    color: '#333333',
    icon: <InfoIcon sx={{ color: '#333333' }} />
  }
};

const StyledSnackbar = styled(SnackbarContent)(({ theme, variant = 'default' }: { theme: any, variant?: string }) => {
  const color = colors[variant as keyof typeof colors] || colors.default;
  
  return {
    backgroundColor: color.bg,
    color: color.color,
    border: `1px solid ${color.color}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    padding: theme.spacing(0, 2),
    minWidth: '300px',
    maxWidth: '400px',
  };
});

export const CustomSnackbar = forwardRef<HTMLDivElement, CustomContentProps>((props, ref) => {
  const {
    id,
    message,
    variant = 'default',
    ...other
  } = props;
  
  // Usar el hook de notistack para cerrar las notificaciones
  const { closeSnackbar } = useSnackbar();
  const color = colors[variant as keyof typeof colors] || colors.default;

  return (
    <NotistackContent ref={ref} {...other}>
      <StyledSnackbar
        message={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box component="span" sx={{ mr: 1.5 }}>
              {color.icon}
            </Box>
            <Typography variant="body2">{message}</Typography>
          </Box>
        }
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => {
              // Usar closeSnackbar del hook con el ID de la notificación
              closeSnackbar(id);
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </NotistackContent>
  );
});

CustomSnackbar.displayName = 'CustomSnackbar';